import type { ArrangementListItemDto } from '@pams/shared';

/**
 * ─── LAYER: lib ──────────────────────────────────────────────────────────────────
 *
 * NESTING A PAGE OF ARRANGEMENTS UNDER THE GROUPS THEY BELONG TO. Pure — no React.
 *
 * ⚠️ THE ASSEMBLY IS CLIENT-SIDE AND THE PAGINATION IS NOT. That combination has one honest
 * consequence and it is stated on screen rather than hidden: a group whose members straddle
 * a page boundary renders as TWO PARTIAL BANDS, each saying how many of its members are on
 * this page. The alternatives were worse — paginating over groups server-side makes `total`
 * mean something different per tab and leaves a mixed sort undefined, and fetching a group's
 * members on expand puts the same arrangement on one page twice, once nested and once flat,
 * with one checkbox state between two rows.
 *
 * ⚠️ AND THE STRADDLE IS RARE FOR A STRUCTURAL REASON WORTH NAMING: every member of a group
 * shares one order date, and the default sort is by that date — so a group's members are
 * ADJACENT in the ordering. The "one order date" rule is not only a semantic constraint; it
 * is what makes client-side grouping viable at all.
 *
 * ⚠️ UNDER ANY OTHER SORT, ADJACENCY IS GONE AND NESTING IS NONSENSE — a group would appear
 * two or three times with one member each. So the list renders FLAT under a non-date sort,
 * every member keeps a marker so nothing is lost, and the table says so.
 */

/**
 * ⚠️ `rowKind`, NOT `kind`. `ArrangementListItemDto` ALREADY HAS A `kind` — STANDARD or
 * ROUND_THE_CLOCK — so a discriminant of that name is silently overwritten by the spread
 * below and every row reads as an arrangement whatever it is. Found by the test that
 * asserts ungrouped rows stay flat, which is the only reason it is not still in here.
 */
export type GroupedRow =
  | {
      rowKind: 'group';
      key: string;
      groupId: string;
      name: string;
      paDate: string;
      /** The group's real size, as far as this caller may know it. */
      memberCount: number;
      /** How many of them are on THIS page. */
      shownCount: number;
      /**
       * ⚠️ FULL `GroupedRow`s, NOT BARE DTOs. AntD nests on `children` and keys every row
       * through the table's `rowKey`, so a child without a `key` renders with an undefined
       * one — which is invisible until a selection or an expansion behaves at random.
       */
      children: Extract<GroupedRow, { rowKind: 'arrangement' }>[];
    }
  | ({ rowKind: 'arrangement'; key: string } & ArrangementListItemDto);

export const groupRows = (items: readonly ArrangementListItemDto[], options: { nest: boolean }): GroupedRow[] => {
  if (!options.nest) return items.map((item) => ({ rowKind: 'arrangement' as const, key: item.id, ...item }));

  const rows: GroupedRow[] = [];
  const emitted = new Map<string, Extract<GroupedRow, { rowKind: 'group' }>>();

  for (const item of items) {
    if (!item.group) {
      rows.push({ rowKind: 'arrangement', key: item.id, ...item });
      continue;
    }

    const already = emitted.get(item.group.id);
    if (already) {
      already.children.push({ rowKind: 'arrangement', key: item.id, ...item });
      already.shownCount += 1;
      continue;
    }

    /* ⚠️ EMITTED AT THE POSITION OF ITS FIRST MEMBER, so the server's ordering is preserved
       and no row jumps to somewhere the operator did not expect to find it. */
    const band: Extract<GroupedRow, { rowKind: 'group' }> = {
      rowKind: 'group',
      key: `group:${item.group.id}`,
      groupId: item.group.id,
      name: item.group.name,
      paDate: item.paDate,
      memberCount: item.group.memberCount,
      shownCount: 1,
      children: [{ rowKind: 'arrangement', key: item.id, ...item }],
    };
    emitted.set(item.group.id, band);
    rows.push(band);
  }

  return rows;
};

/** `true` when a band is showing only part of its group — the sentence is on the row. */
export const isPartialGroup = (row: Extract<GroupedRow, { rowKind: 'group' }>): boolean =>
  row.shownCount < row.memberCount;
